import { z } from 'zod';

/**
 * Who works in a conversation: one agent, or a group of the user's assistants (personas), each
 * answering from its own conversation (`@crowdex/groups`).
 */
export const crowdexCollaborationSchema = z.enum(['solo', 'group']);

/** A conversation's labels: free text the sidebar filters on (a plugin's name, a run's). */
export const conversationTagSchema = z.string().trim().min(1).max(64);
export const conversationTagsSchema = z.array(conversationTagSchema).max(8);

export const crowdexPermissionModeSchema = z.enum(['allow-reads', 'allow-edits', 'allow-all']);
export type CrowdexPermissionMode = z.infer<typeof crowdexPermissionModeSchema>;

export const crowdexSessionConfigValuesSchema = z.record(
  z.string(),
  z.union([z.string(), z.boolean()]),
);

export const crowdexNativeAgentSchema = z.object({
  id: z.string().regex(/^[a-zA-Z0-9._-]{1,128}$/),
  command: z.string().min(1),
  args: z.array(z.string()),
  env: z.record(z.string(), z.string()),
});

const crowdexMcpHeadersSchema = z.array(z.object({ name: z.string().min(1), value: z.string() }));

/** Portable ACP MCP descriptors owned by the CLI session boundary. */
export const crowdexAgentMcpServerSchema = z.union([
  z.object({
    type: z.literal('http'),
    name: z.string().min(1),
    url: z.url(),
    headers: crowdexMcpHeadersSchema,
  }),
  z.object({
    name: z.string().min(1),
    command: z.string().min(1),
    args: z.array(z.string()),
    env: z.array(z.object({ name: z.string().min(1), value: z.string() })),
  }),
]);

/**
 * The plugins' MCP servers an agent uses, of those its host serves: all (`inherit`, the default),
 * none, or its picks (`plugin:<name>` for all of a plugin's servers, else a server's own name).
 */
export const crowdexAgentMcpPolicySchema = z
  .object({
    mode: z.enum(['inherit', 'custom', 'none']),
    serverNames: z.array(z.string().min(1).max(256)).max(128).optional(),
  })
  .strict();
export type CrowdexAgentMcpPolicy = z.infer<typeof crowdexAgentMcpPolicySchema>;

/**
 * Capabilities attached to the execution host for the lifetime of one task.
 * The agent server snapshots this manifest when it creates the session; tools
 * are never routed to a different host after the session has started.
 */
export const pluginClientToolSchema = z
  .object({
    name: z.string().regex(/^[a-zA-Z0-9_.-]{1,128}$/),
    description: z.string().max(32000),
    inputSchema: z.object({ type: z.literal('object') }).catchall(z.unknown()),
    /** The code-mode namespace the tool is in (`host` when omitted). */
    namespace: z
      .string()
      .regex(/^[a-z][a-z0-9_]{0,31}$/)
      .optional(),
  })
  .strict();
export type PluginClientTool = z.infer<typeof pluginClientToolSchema>;

export const crowdexAgentHostManifestSchema = z.object({
  environment: z.enum(['local', 'cloud']),
  includesClientTools: z.boolean().default(false),
  clientTools: z.array(pluginClientToolSchema).max(128).optional(),
  mcpServers: z.array(crowdexAgentMcpServerSchema).max(128).default([]),
  mcp: crowdexAgentMcpPolicySchema.optional(),
  /** Optional host-approved runtimes and bounds for core cross-ACP delegation. */
  delegation: z
    .object({
      agents: z
        .array(crowdexNativeAgentSchema.extend({ name: z.string().optional() }))
        .max(64)
        .optional(),
      concurrency: z.number().int().min(1).max(16).optional(),
      depth: z.number().int().min(1).max(8).optional(),
    })
    .optional(),
});

export const crowdexOutputSchemaSchema = z.record(z.string(), z.unknown());

const agentRequestBaseSchema = z
  .object({
    conversationId: z.string().min(1).max(128),
    modelId: z.string().trim().min(1).optional(),
    /**
     * The model the user runs each of their agents on (by canonical agent id): an agent the
     * conversation delegates to without naming a model runs that one, never its own default.
     */
    agentModels: z.record(z.string().min(1).max(128), z.string().trim().min(1).max(256)).optional(),
    instructions: z.string().optional(),
    /**
     * The workspace globs of the account's artifact types: after each turn, a cloud execution
     * reports the files matching them that changed.
     */
    artifactGlobs: z.array(z.string().min(1).max(256)).max(64).optional(),
    permissionMode: crowdexPermissionModeSchema.default('allow-all'),
    sessionConfigValues: crowdexSessionConfigValuesSchema.optional(),
    outputSchema: crowdexOutputSchemaSchema.optional(),
  })
  .strict();

export const crowdexLocalAgentRequestSchema = agentRequestBaseSchema.extend({
  agent: crowdexNativeAgentSchema,
  workspaceRoot: z.string().min(1).max(4096),
  hostManifest: crowdexAgentHostManifestSchema,
});

/** The base an execution checkpoints its workspace against when a turn ends. */
export const executionTurnCheckpointSchema = z
  .object({
    baseRef: z.string().min(1).max(512),
    baseCommitSha: z.string().regex(/^[0-9a-f]{40}$/),
    kind: z.enum(['patch', 'archive']),
  })
  .strict();

export const crowdexLocalAgentTurnRequestSchema = crowdexLocalAgentRequestSchema.extend({
  turnId: z.uuid(),
  messages: z.array(z.unknown()).min(1).max(512),
  systemReminder: z.string().trim().min(1).max(10_000).optional(),
  /** Cloud executions: checkpoint the workspace against this base after the turn. */
  checkpoint: executionTurnCheckpointSchema.optional(),
  /**
   * The user edited an earlier message: `messages` ends with it, and the turns after it are
   * gone. The agent's session forgets them too (a fresh one replays this transcript).
   */
  rewind: z.literal(true).optional(),
  /**
   * The conversations this one was delegated by, its parent first: a turn of theirs running in
   * the same folder takes this turn's work as its own.
   */
  parentConversationIds: z.array(z.string().min(1).max(128)).max(8).optional(),
});

/**
 * Generates from the conversation's own agent session instead of a fresh one: the
 * session is branched (all its context, the provider's prompt cache) and the branch
 * is discarded. Only agents that can branch a session serve it.
 */
const forkConversationSchema = z.boolean().optional();

export const crowdexLocalAgentGenerateRequestSchema = crowdexLocalAgentRequestSchema.extend({
  prompt: z.string().trim().min(1).max(100_000),
  includeDetails: z.boolean().optional(),
  forkConversation: forkConversationSchema,
});

/** Controls one exact active turn inside a durable Crowdex conversation. */
export const crowdexAgentControlRequestSchema = z.object({
  conversationId: z.string().min(1).max(128),
  turnId: z.uuid(),
});

export const crowdexAgentSteerRequestSchema = crowdexAgentControlRequestSchema.extend({
  text: z.string().trim().min(1).max(100_000),
  messageId: z.string().trim().min(1).max(200).optional(),
});

export const crowdexAgentInteractionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('approval'),
    id: z.string().min(1).max(256),
    approved: z.boolean(),
    /** Approve this kind of operation from now on, when the request offers it (`remember`). */
    remember: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('elicitation'),
    requestId: z.string().min(1).max(256),
    outcome: z.discriminatedUnion('action', [
      z.object({
        action: z.literal('accept'),
        content: z
          .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]))
          .optional(),
      }),
      z.object({ action: z.literal('decline') }),
      z.object({ action: z.literal('cancel') }),
    ]),
  }),
  z.object({
    type: z.literal('tool-result'),
    toolCallId: z.string().min(1).max(256),
    output: z.unknown().optional(),
    errorText: z.string().max(100_000).optional(),
  }),
]);

export const crowdexAgentInteractionRequestSchema = crowdexAgentControlRequestSchema.extend({
  interaction: crowdexAgentInteractionSchema,
});

const cloudAgentRequestBaseSchema = agentRequestBaseSchema.extend({
  conversationId: z.uuid(),
  agentId: z.string().regex(/^[a-zA-Z0-9._-]{1,128}$/),
  /** The plugins' MCP servers its agent uses (see the local `hostManifest.mcp`). */
  mcp: crowdexAgentMcpPolicySchema.optional(),
  /** Its agent delegates to copies of itself only, not to the host's other agents. */
  solo: z.literal(true).optional(),
});

export const crowdexCloudAgentTurnRequestSchema = cloudAgentRequestBaseSchema.extend({
  turnId: z.uuid(),
  messages: z.array(z.unknown()).min(1).max(512),
  systemReminder: z.string().trim().min(1).max(10_000).optional(),
  /** See {@link crowdexLocalAgentTurnRequestSchema}: the turns after the edited message are gone. */
  rewind: z.literal(true).optional(),
  persistence: z
    .object({
      title: z.string().trim().min(1).max(140),
      collaboration: crowdexCollaborationSchema,
      workspaceRoot: z.string().min(1).max(4096),
      agentName: z.string().trim().min(1).max(64),
    })
    .optional(),
});

/**
 * A local-profile server's turn: the local agent request (this machine's agent
 * and workspace), which the server persists like a cloud turn before relaying
 * it to its workspace server.
 */
export const crowdexLocalServerAgentTurnRequestSchema = crowdexLocalAgentTurnRequestSchema.extend({
  conversationId: z.uuid(),
  persistence: z
    .object({
      title: z.string().trim().min(1).max(140),
      collaboration: crowdexCollaborationSchema,
      workspaceRoot: z.string().min(1).max(4096),
      workspaceName: z.string().trim().min(1).max(200),
      agentName: z.string().trim().min(1).max(64),
      runtime: z.enum(['local', 'worktree']),
      /** Labels the new conversation starts with (the plugin that started it). */
      tags: conversationTagsSchema.optional(),
    })
    .optional(),
});

/**
 * What is saved with a conversation and equips each of its turns, whoever starts them: the
 * account's saved agent it works as, its model, its agent's options, its permissions.
 */
export const conversationSettingsSchema = z
  .object({
    agentProfileId: z.string().min(1).max(128).nullable(),
    modelId: z.string().trim().min(1).max(256).nullable(),
    sessionConfigValues: crowdexSessionConfigValuesSchema,
    permissionMode: crowdexPermissionModeSchema,
  })
  .strict();
export type ConversationSettings = z.infer<typeof conversationSettingsSchema>;

/** What only the page knows of a turn: the app the user works with, a handoff's context. */
export const pageTurnContextSchema = z
  .object({ systemReminder: z.string().trim().min(1).max(10_000).optional() })
  .strict();

/** Where a page's conversation runs: this machine's agent in a folder, or a cloud agent. */
export const pageAgentPlaceSchema = z.union([
  z
    .object({ agent: crowdexNativeAgentSchema, workspaceRoot: z.string().min(1).max(4096) })
    .strict(),
  z.object({ agentId: z.string().regex(/^[a-zA-Z0-9._-]{1,128}$/) }).strict(),
]);

const pageAgentRequestSchema = z
  .object({
    conversationId: z.uuid(),
    place: pageAgentPlaceSchema,
    settings: conversationSettingsSchema,
    page: pageTurnContextSchema.optional(),
  })
  .strict();

/**
 * A turn the page sends (`/api/agent/turns`): its message and the conversation's settings, which
 * the server saves. The server equips it as any turn of the conversation.
 */
export const pageAgentTurnRequestSchema = pageAgentRequestSchema.extend({
  turnId: z.uuid(),
  /**
   * `@persona` (design D51): this turn is the saved agent's, on the conversation's own runtime;
   * the conversation's settings stay as they are, its reply is marked as the persona's.
   */
  persona: z
    .object({ id: z.string().regex(/^[a-zA-Z0-9._-]{1,128}$/) })
    .strict()
    .optional(),
  messages: z.array(z.unknown()).min(1).max(512),
  rewind: z.literal(true).optional(),
  persistence: z
    .object({
      title: z.string().trim().min(1).max(140),
      collaboration: crowdexCollaborationSchema,
      workspaceRoot: z.string().min(1).max(4096),
      agentName: z.string().trim().min(1).max(64),
      /** Local conversations: the folder's display name and whether it is a Git worktree. */
      workspaceName: z.string().trim().min(1).max(200).optional(),
      runtime: z.enum(['local', 'worktree']).optional(),
    })
    .strict()
    .optional(),
});
export type PageAgentTurnRequest = z.infer<typeof pageAgentTurnRequestSchema>;

/** The page readying a conversation's agent session before its message (`/api/agent/prepare`). */
export const pageAgentPrepareRequestSchema = pageAgentRequestSchema;

/** A one-off generation of the page (`/api/agent/generate`), equipped as the conversation's turns. */
export const pageAgentGenerateRequestSchema = pageAgentRequestSchema.extend({
  prompt: z.string().trim().min(1).max(100_000),
  outputSchema: crowdexOutputSchemaSchema.optional(),
  forkConversation: forkConversationSchema,
});
export type PageAgentGenerateRequest = z.infer<typeof pageAgentGenerateRequestSchema>;

export type CrowdexLocalServerAgentTurnRequest = z.infer<
  typeof crowdexLocalServerAgentTurnRequestSchema
>;
export type CrowdexLocalAgentRequest = z.infer<typeof crowdexLocalAgentRequestSchema>;
export type CrowdexAgentHostManifest = z.infer<typeof crowdexAgentHostManifestSchema>;
export type CrowdexLocalAgentTurnRequest = z.infer<typeof crowdexLocalAgentTurnRequestSchema>;
export type CrowdexLocalAgentGenerateRequest = z.infer<
  typeof crowdexLocalAgentGenerateRequestSchema
>;
export type CrowdexCloudAgentTurnRequest = z.infer<typeof crowdexCloudAgentTurnRequestSchema>;
export type CrowdexAgentControlRequest = z.infer<typeof crowdexAgentControlRequestSchema>;
export type CrowdexAgentSteerRequest = z.infer<typeof crowdexAgentSteerRequestSchema>;
export type CrowdexAgentInteraction = z.infer<typeof crowdexAgentInteractionSchema>;
export type CrowdexAgentInteractionRequest = z.infer<typeof crowdexAgentInteractionRequestSchema>;
